import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtWhen } from "../../lib/peApi";
import { usePEAuth } from "./PEAuthContext";

const inputCls =
  "w-full rounded-sm border border-pe-sand/70 bg-white px-3.5 py-2.5 text-sm text-pe-forest outline-none focus:border-pe-gold transition-colors";

const TABS = [
  ["overview", "Overview"], ["appointments", "Appointments"], ["clients", "Clients"], ["payments", "Payments"],
  ["reviews", "Reviews"], ["notes", "Notes"], ["rewards", "Rewards & Referrals"], ["promotions", "Promotions"],
  ["campaigns", "Campaigns"], ["reminders", "Reminders"], ["settings", "Settings"], ["audit", "Audit Log"],
];

const STATUS_OPTIONS = ["confirmed", "completed", "canceled", "no_show", "pending_review"];

function Stat({ label, value }) {
  return (
    <div className="rounded-sm border border-pe-sand/60 bg-white p-5">
      <p className="text-[10px] tracking-[0.25em] uppercase text-pe-olive mb-1">{label}</p>
      <p className="font-pe text-3xl text-pe-forest">{value}</p>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left font-sans text-[10px] tracking-[0.2em] uppercase text-pe-olive px-3 py-2 border-b border-pe-sand/60">{children}</th>;
}

function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 text-[13px] text-pe-body border-b border-pe-sand/40 align-top ${className}`}>{children}</td>;
}

export default function PEAdminPage() {
  const { user } = usePEAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [data, setData] = useState(null);
  const [dataTab, setDataTab] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const isStaff = user && user.role && user.role !== "client";
  useEffect(() => {
    if (user === null) navigate("/prismatic-essence/auth");
  }, [user, navigate]);

  const load = async () => {
    setErr("");
    try {
      let result = null;
      if (tab === "overview") result = await api("/admin/overview");
      else if (tab === "appointments") result = await api("/admin/appointments");
      else if (tab === "clients") result = await api("/admin/clients");
      else if (tab === "payments") result = await api("/admin/payments");
      else if (tab === "reviews") result = await api("/admin/reviews");
      else if (tab === "notes") result = await api("/admin/notes");
      else if (tab === "rewards") result = { referrals: (await api("/admin/referrals")).referrals, settings: await api("/admin/settings") };
      else if (tab === "promotions") result = await api("/admin/promotions");
      else if (tab === "campaigns") result = await api("/admin/campaigns");
      else if (tab === "reminders") result = await api("/admin/reminders");
      else if (tab === "settings") result = await api("/admin/settings");
      else if (tab === "audit") result = await api("/admin/audit");
      setData(result);
      setDataTab(tab);
    } catch (e) {
      setErr(e.message);
      setData(null);
      setDataTab(null);
    }
  };

  useEffect(() => {
    if (isStaff) load();
  }, [isStaff, tab]);

  if (!user) return <div className="min-h-screen bg-pe-ivory flex items-center justify-center"><p className="font-pe italic text-xl text-pe-olive">Checking access…</p></div>;
  if (!isStaff) {
    return (
      <div className="min-h-screen bg-pe-ivory flex items-center justify-center px-5 text-center">
        <div>
          <p className="font-pe text-2xl text-pe-forest mb-3">This room is for the house.</p>
          <Link to="/prismatic-essence/account" className="text-pe-brass underline underline-offset-4 text-sm">Back to your dashboard</Link>
        </div>
      </div>
    );
  }

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 4000); };

  return (
    <main id="main-content" data-testid="pe-admin" className="min-h-screen bg-pe-cream py-24 px-5">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <Link to="/prismatic-essence" className="text-[10px] tracking-[0.25em] uppercase text-pe-muted hover:text-pe-forest">← Prismatic Essence</Link>
            <h1 className="font-pe text-3xl md:text-4xl text-pe-forest mt-3">The Back Office</h1>
            <p className="text-[11px] tracking-[0.2em] uppercase text-pe-muted mt-1">{user.email} · {user.role.replace(/_/g, " ")}</p>
          </div>
        </div>

        {msg && <p role="status" aria-live="polite" className="mb-5 text-sm text-pe-forest bg-white border border-pe-gold/40 rounded-sm px-4 py-3">{msg}</p>}
        {err && <p className="mb-5 text-sm text-red-800 bg-red-50 border border-red-200 rounded-sm px-4 py-3">{err}</p>}

        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map(([id, label]) => (
            <button key={id} data-testid={`pe-admin-tab-${id}`} onClick={() => setParams({ tab: id }, { replace: true })}
              className={`rounded-full px-3.5 py-2 font-sans text-[10px] tracking-[0.18em] uppercase border transition-colors ${tab === id ? "bg-pe-forest text-pe-ivory border-pe-forest" : "border-pe-sand/70 bg-white text-pe-olive hover:border-pe-gold"}`}>
              {label}
            </button>
          ))}
        </div>

        {!data && !err && <p className="font-pe italic text-lg text-pe-olive">Loading…</p>}

        {data && dataTab === "overview" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Clients" value={data.clients} />
            <Stat label="Upcoming" value={data.upcoming} />
            <Stat label="Pending payment" value={data.pending_payment} />
            <Stat label="Pending review" value={data.pending_review} />
            <Stat label="Reviews to moderate" value={data.reviews_pending} />
            <Stat label="New notes" value={data.notes_new} />
            <Stat label="Reminders scheduled" value={data.reminders_scheduled} />
            <Stat label="Deposits collected" value={fmtMoney(data.deposits)} />
          </div>
        )}

        {data && dataTab === "appointments" && <AppointmentsTab data={data} reload={load} flash={flash} />}
        {data && dataTab === "clients" && <ClientsTab data={data} />}
        {data && dataTab === "payments" && (
          <div className="overflow-x-auto bg-white border border-pe-sand/60 rounded-sm">
            <table className="w-full">
              <thead><tr><Th>Client / Booking</Th><Th>Kind</Th><Th>Amount</Th><Th>Status</Th><Th>Stripe ref</Th><Th>Date</Th></tr></thead>
              <tbody>
                {data.payments.map((p) => (
                  <tr key={p.session_id}>
                    <Td>{p.booking_id}</Td><Td>{p.kind}</Td><Td>{fmtMoney(p.amount)}</Td>
                    <Td><span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${p.payment_status === "paid" ? "bg-pe-cream text-pe-forest" : "bg-amber-50 text-amber-800"}`}>{p.payment_status}</span></Td>
                    <Td className="font-mono text-[11px]">{p.stripe_payment_intent_id || "—"}</Td>
                    <Td>{new Date(p.created_at).toLocaleDateString()}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!data.payments.length && <p className="p-6 text-sm text-pe-muted">No payments yet.</p>}
          </div>
        )}
        {data && dataTab === "reviews" && <ReviewsTab data={data} reload={load} flash={flash} />}
        {data && dataTab === "notes" && <NotesAdminTab data={data} reload={load} flash={flash} />}
        {data && dataTab === "rewards" && <RewardsTab data={data} reload={load} flash={flash} />}
        {data && dataTab === "promotions" && <PromotionsTab data={data} reload={load} flash={flash} />}
        {data && dataTab === "campaigns" && <CampaignsTab data={data} reload={load} flash={flash} />}
        {data && dataTab === "reminders" && (
          <div className="overflow-x-auto bg-white border border-pe-sand/60 rounded-sm">
            <table className="w-full">
              <thead><tr><Th>Appointment</Th><Th>Interval</Th><Th>Send at</Th><Th>Status</Th><Th>Email / SMS</Th></tr></thead>
              <tbody>
                {data.reminders.map((r) => (
                  <tr key={`${r.appointment_id}-${r.interval}`}>
                    <Td>{r.appointment_id}</Td><Td>{r.interval}</Td><Td>{fmtWhen(r.send_at)}</Td>
                    <Td><span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${r.status === "sent" ? "bg-pe-cream text-pe-forest" : r.status === "failed" ? "bg-red-50 text-red-800" : "bg-amber-50 text-amber-800"}`}>{r.status}</span></Td>
                    <Td>{r.email_status || "—"} / {r.sms_status || "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!data.reminders.length && <p className="p-6 text-sm text-pe-muted">No reminders scheduled yet.</p>}
          </div>
        )}
        {data && dataTab === "settings" && <SettingsTab data={data} reload={load} flash={flash} />}
        {data && dataTab === "audit" && (
          <div className="overflow-x-auto bg-white border border-pe-sand/60 rounded-sm">
            <table className="w-full">
              <thead><tr><Th>When</Th><Th>Actor</Th><Th>Action</Th><Th>Entity</Th><Th>Reason</Th></tr></thead>
              <tbody>
                {data.audit.map((a, i) => (
                  <tr key={i}><Td>{fmtWhen(a.at)}</Td><Td>{a.actor_email}</Td><Td>{a.action}</Td><Td>{a.entity} {a.entity_id}</Td><Td>{a.reason || "—"}</Td></tr>
                ))}
              </tbody>
            </table>
            {!data.audit.length && <p className="p-6 text-sm text-pe-muted">No administrative actions recorded yet.</p>}
          </div>
        )}
      </div>
    </main>
  );
}

function AppointmentsTab({ data, reload, flash }) {
  const [draft, setDraft] = useState({});
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [priceDraft, setPriceDraft] = useState({});

  const act = async (booking_id, patch) => {
    try { await api(`/admin/appointments/${booking_id}`, { method: "PATCH", body: patch }); await reload(); flash("Appointment updated."); }
    catch (e) { alert(e.message); }
  };
  const openDetail = async (booking_id) => {
    if (open === booking_id) { setOpen(null); return; }
    setOpen(booking_id);
    setDetail(null);
    try { setDetail(await api(`/admin/appointments/${booking_id}/detail`)); } catch (e) { alert(e.message); }
  };
  const savePrice = async (booking_id) => {
    const d = priceDraft[booking_id] || {};
    try {
      await api(`/admin/appointments/${booking_id}/price`, { method: "POST", body: { final_price: Number(d.price), reason: d.reason || "" } });
      setPriceDraft((p) => ({ ...p, [booking_id]: {} }));
      await reload();
      await openDetail(booking_id); // refresh detail in place
      flash("Final price confirmed and recorded.");
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="space-y-3">
      {data.appointments.map((a) => (
        <div key={a.booking_id} data-testid={`pe-admin-appointment-${a.booking_id}`} className="rounded-sm border border-pe-sand/60 bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-pe text-xl text-pe-forest">{a.service.name} {a.client_type === "guest" && <span className="rounded-full border border-pe-sand px-2 py-0.5 text-[9px] tracking-[0.15em] uppercase text-pe-olive align-middle">guest</span>}</p>
              <p className="text-[13px] text-pe-muted">{a.client_name} · {a.client_email} · {a.client_phone || "no phone"}</p>
              <p className="text-[13px] text-pe-muted">{fmtWhen(a.start)} · {a.service.duration_label} · deposit {a.payment_status === "paid" ? "paid" : a.payment_status}{a.deposit_transferred ? " (transferred)" : ""}</p>
              {(a.quote?.addons || []).length > 0 && (
                <p className="text-[12px] text-pe-muted mt-1">Selections: {a.quote.addons.map((x) => `${x.label}${x.amount ? ` +$${x.amount}` : ""}`).join(" · ")}</p>
              )}
              <p className="text-[12px] text-pe-muted mt-1">
                {a.quote?.subtotal ? `Total ${fmtMoney(a.quote.subtotal)}` : a.quote?.service_price ? `Service ${fmtMoney(a.quote.service_price)}` : "Price at consultation"}
                {a.quote?.discount ? ` · ${a.quote.discount.label} (−${fmtMoney(a.quote.discount.amount)})` : ""}
                {a.quote?.balance !== null && a.quote?.balance !== undefined ? ` · Balance ${fmtMoney(a.quote.balance)}` : " · Balance pending confirmation"}
                {a.quote?.final_price ? ` · FINAL ${fmtMoney(a.quote.final_price)}` : ""}
              </p>
              {a.cancel_fee ? <p className="text-[12px] text-red-800 mt-1">Late-cancellation fee: ${a.cancel_fee} ({a.cancel_fee_status}) · {a.cancel_hours_before}h before start</p> : null}
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className="rounded-full border border-pe-sand px-3 py-1 text-[10px] tracking-[0.15em] uppercase">{a.status.replace(/_/g, " ")}</span>
              <button data-testid={`pe-admin-detail-${a.booking_id}`} onClick={() => openDetail(a.booking_id)} className="rounded-full border border-pe-sand px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase text-pe-olive hover:border-pe-gold">
                {open === a.booking_id ? "Close details" : "Details"}
              </button>
              <div className="flex gap-2">
                <select
                  data-testid={`pe-admin-status-${a.booking_id}`}
                  className="rounded-sm border border-pe-sand/70 px-2 py-1.5 text-xs"
                  value={draft[a.booking_id]?.status || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, [a.booking_id]: { ...d[a.booking_id], status: e.target.value } }))}
                >
                  <option value="">Set status…</option>
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
                <button
                  data-testid={`pe-admin-apply-${a.booking_id}`}
                  onClick={() => {
                    const status = draft[a.booking_id]?.status;
                    if (!status) return;
                    let reason = "";
                    if (["canceled", "rescheduled"].includes(status)) reason = window.prompt("Reason (recorded in the audit log):") || "";
                    act(a.booking_id, { status, reason });
                  }}
                  className="rounded-full bg-pe-forest text-pe-ivory px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>

          {open === a.booking_id && (
            <div className="mt-4 pt-4 border-t border-pe-sand/50 grid md:grid-cols-2 gap-5" data-testid={`pe-admin-detail-panel-${a.booking_id}`}>
              {!detail || detail.appointment.booking_id !== a.booking_id ? (
                <p className="font-pe italic text-pe-olive">Loading details…</p>
              ) : (
                <>
                  <div className="space-y-3 text-[13px] text-pe-body">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-pe-olive">Preferences</p>
                    {(detail.appointment.prefs?.music_genres || []).length > 0 && <p>Music: {detail.appointment.prefs.music_genres.join(", ")}{detail.appointment.prefs.music_other ? ` (also: ${detail.appointment.prefs.music_other})` : ""}</p>}
                    <p>Volume: {(detail.appointment.prefs?.music_volume || "no-preference").replace(/-/g, " ")} · Conversation: {(detail.appointment.prefs?.conversation || "no-preference").replace(/-/g, " ")}</p>
                    {(detail.appointment.prefs?.entertainment || []).length > 0 && <p>Entertainment: {detail.appointment.prefs.entertainment.join(", ")}</p>}
                    {detail.appointment.prefs?.music_artists && <p>Artists/songs: {detail.appointment.prefs.music_artists}</p>}
                    {detail.appointment.prefs?.comfort_note && <p className="italic text-pe-olive">"{detail.appointment.prefs.comfort_note}"</p>}
                    {detail.appointment.safety_note && <p className="text-red-800">Safety note: {detail.appointment.safety_note}</p>}
                    {(detail.appointment.price_history || []).length > 0 && (
                      <div>
                        <p className="text-[10px] tracking-[0.2em] uppercase text-pe-olive mt-2">Price history</p>
                        {detail.appointment.price_history.map((h, i) => (
                          <p key={i} className="text-[12px]">{fmtMoney(h.final_price)} — {h.reason} ({h.by}, {new Date(h.at).toLocaleDateString()})</p>
                        ))}
                      </div>
                    )}
                    <div className="rounded-sm border border-pe-gold/40 bg-pe-cream/50 p-4 space-y-2 mt-2">
                      <p className="text-[10px] tracking-[0.2em] uppercase text-pe-olive">Confirm / adjust final price</p>
                      <div className="flex gap-2">
                        <input type="number" min="0" step="1" placeholder={detail.appointment.quote?.subtotal ? `calc. ${detail.appointment.quote.subtotal}` : "final $"} className={inputCls} value={priceDraft[a.booking_id]?.price || ""} onChange={(e) => setPriceDraft((p) => ({ ...p, [a.booking_id]: { ...p[a.booking_id], price: e.target.value } }))} />
                        <input placeholder="Reason (recorded)" className={inputCls} value={priceDraft[a.booking_id]?.reason || ""} onChange={(e) => setPriceDraft((p) => ({ ...p, [a.booking_id]: { ...p[a.booking_id], reason: e.target.value } }))} />
                      </div>
                      <button data-testid={`pe-admin-price-save-${a.booking_id}`} onClick={() => savePrice(a.booking_id)} className="rounded-full bg-pe-forest text-pe-ivory px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase">Confirm price</button>
                    </div>
                  </div>
                  <div className="text-[13px] text-pe-body">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-2">Required forms</p>
                    {(detail.appointment.form_responses || []).map((f) => (
                      <div key={f.form_key} className="mb-3">
                        <p className="font-medium text-pe-forest">{f.form_key === "consultation" ? "Consultation Form" : "Client Intake Form"} <span className="text-pe-muted font-normal">· {f.version} · {f.updated_at ? new Date(f.updated_at).toLocaleDateString() : ""}</span></p>
                        {(f.flags || []).length === 0 && <p className="text-pe-muted text-[12px]">No safety flags.</p>}
                        {(f.flags || []).map((fl, i) => (
                          <p key={i} className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 mt-1">
                            <strong>{fl.level_label}</strong> — {fl.note} <span className="text-pe-muted">(safety flag, not a confirmed contraindication)</span>
                          </p>
                        ))}
                      </div>
                    ))}
                    {!(detail.appointment.form_responses || []).length && <p className="text-pe-muted text-[12px]">No form responses on file for this client.</p>}
                    <p className="text-[11px] text-pe-muted mt-3">Flags guide professional review only — they never diagnose, never clear, and never override your judgment.</p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ))}
      {!data.appointments.length && <p className="text-sm text-pe-muted">No appointments yet.</p>}
    </div>
  );
}

function ClientsTab({ data }) {
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const openClient = async (id) => {
    setOpen(id);
    setDetail(await api(`/admin/clients/${id}`));
  };
  return (
    <div className="space-y-3">
      {data.clients.map((c) => (
        <div key={c.user_id} className="rounded-sm border border-pe-sand/60 bg-white p-5">
          <button className="w-full text-left flex flex-wrap justify-between gap-3" onClick={() => (open === c.user_id ? setOpen(null) : openClient(c.user_id))}>
            <span className="font-pe text-lg text-pe-forest">{c.name} {c.preferred_name ? `(${c.preferred_name})` : ""}</span>
            <span className="text-[12px] text-pe-muted">{c.email} · {c.phone || "no phone"} · code {c.referral_code} · marketing {c.marketing_opt_in ? "on" : "off"}</span>
          </button>
          {open === c.user_id && detail && detail.client.user_id === c.user_id && (
            <div className="mt-4 pt-4 border-t border-pe-sand/50 grid md:grid-cols-2 gap-5 text-[13px] text-pe-body">
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-2">Preferences</p>
                {(c.prefs?.music_genres || []).length > 0 && <p>Music: {c.prefs.music_genres.join(", ")}</p>}
                <p>Volume: {(c.prefs?.music_volume || "no-preference").replace(/-/g, " ")} · Conversation: {(c.prefs?.conversation || "no-preference").replace(/-/g, " ")}</p>
                {(c.prefs?.entertainment || []).length > 0 && <p>Entertainment: {c.prefs.entertainment.join(", ")}</p>}
                {c.prefs?.comfort_note && <p className="italic text-pe-olive mt-1">"{c.prefs.comfort_note}"</p>}
                <p className="mt-3 text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-1">Required forms</p>
                {(detail.form_responses || []).map((f) => (
                  <div key={f.form_key} className="mb-2">
                    <p className="text-pe-forest">{f.form_key} — v{f.version} · {f.updated_at ? new Date(f.updated_at).toLocaleDateString() : ""} · {(f.history || []).length} earlier version(s)</p>
                    {(f.flags || []).map((fl, i) => (
                      <p key={i} className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 mt-1"><strong>{fl.level_label}</strong> — {fl.note}</p>
                    ))}
                  </div>
                ))}
                {!(detail.form_responses || []).length && <p className="text-pe-muted">No form responses on file.</p>}
              </div>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-2">History</p>
                {detail.appointments.slice(0, 6).map((a) => <p key={a.booking_id}>{a.service.name} — {new Date(a.start).toLocaleDateString()} — {a.status.replace(/_/g, " ")}</p>)}
                {!detail.appointments.length && <p className="text-pe-muted">No appointments yet.</p>}
              </div>
            </div>
          )}
        </div>
      ))}
      {!data.clients.length && <p className="text-sm text-pe-muted">No clients yet.</p>}
    </div>
  );
}

function ReviewsTab({ data, reload, flash }) {
  const setStatus = async (review_id, status) => {
    await api(`/admin/reviews/${review_id}`, { method: "PATCH", body: { status } });
    await reload();
    flash(`Review ${status}.`);
  };
  return (
    <div className="space-y-3">
      {data.reviews.map((r) => (
        <div key={r.review_id} className="rounded-sm border border-pe-sand/60 bg-white p-5">
          <div className="flex flex-wrap justify-between gap-3">
            <div>
              <p className="font-pe text-lg text-pe-forest">{r.service} <span className="text-pe-brass">{"★".repeat(r.rating)}</span></p>
              <p className="text-sm text-pe-body mt-1">{r.text}</p>
              <p className="text-[11px] text-pe-muted mt-2">{r.client_name} · {new Date(r.appointment_date).toLocaleDateString()} · publish consent: {r.consent_public ? "yes" : "no"} · marketing consent: {r.consent_marketing ? "yes" : "no"}</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="rounded-full border border-pe-sand px-3 py-1 text-[10px] uppercase tracking-wider">{r.status}</span>
              {["approved", "rejected", "archived"].map((s) => (
                <button key={s} data-testid={`pe-admin-review-${s}-${r.review_id}`} onClick={() => setStatus(r.review_id, s)} className="rounded-full border border-pe-brass/60 px-3 py-1 text-[10px] uppercase tracking-wider text-pe-forest hover:bg-pe-gold/10">{s}</button>
              ))}
            </div>
          </div>
        </div>
      ))}
      {!data.reviews.length && <p className="text-sm text-pe-muted">No reviews yet.</p>}
    </div>
  );
}

function NotesAdminTab({ data, reload, flash }) {
  const [resp, setResp] = useState({});
  return (
    <div className="space-y-3">
      {data.notes.map((n) => (
        <div key={n.note_id} className="rounded-sm border border-pe-sand/60 bg-white p-5">
          <div className="flex justify-between gap-3 mb-2">
            <span className="text-[10px] tracking-[0.2em] uppercase text-pe-olive">{n.kind.replace(/_/g, " ")} — {n.client_name}</span>
            <span className="text-[10px] text-pe-muted">{new Date(n.created_at).toLocaleDateString()} · {n.status}</span>
          </div>
          <p className="text-sm text-pe-body whitespace-pre-wrap">{n.text}</p>
          <div className="flex gap-2 mt-3">
            <input className={inputCls} placeholder="Internal response (optional)…" value={resp[n.note_id] || ""} onChange={(e) => setResp((r) => ({ ...r, [n.note_id]: e.target.value }))} />
            <button
              onClick={async () => { await api(`/admin/notes/${n.note_id}`, { method: "PATCH", body: { status: "reviewed", staff_response: resp[n.note_id] || "" } }); await reload(); flash("Note marked reviewed."); }}
              className="rounded-full bg-pe-forest text-pe-ivory px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase shrink-0"
            >
              Mark reviewed
            </button>
          </div>
        </div>
      ))}
      {!data.notes.length && <p className="text-sm text-pe-muted">No notes yet.</p>}
    </div>
  );
}

function RewardsTab({ data, reload, flash }) {
  const s = data.settings;
  const [adj, setAdj] = useState({ user_id: "", points: "", reason: "" });
  const saveSettings = async (patch, label) => {
    await api("/admin/settings", { method: "PUT", body: patch });
    await reload();
    flash(label);
  };
  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-5">
        <div className="rounded-sm border border-pe-sand/60 bg-white p-6 space-y-3">
          <p className="font-pe text-xl text-pe-forest">Loyalty</p>
          <label className="block text-sm"><span className="text-pe-muted text-[12px]">Points per completed visit</span>
            <input type="number" className={inputCls} defaultValue={s.loyalty.points_per_completed_visit} onBlur={(e) => saveSettings({ loyalty: { ...s.loyalty, points_per_completed_visit: Number(e.target.value) } }, "Loyalty rule saved.")} />
          </label>
          <p className="text-[12px] text-pe-muted">Redemption value: <strong>not configured</strong> — set it here before publishing any conversion to clients.</p>
          <div className="border-t border-pe-sand/50 pt-3 space-y-2">
            <p className="text-[12px] text-pe-muted">Manual adjustment (reason required, recorded in audit log):</p>
            <input className={inputCls} placeholder="Client user_id" value={adj.user_id} onChange={(e) => setAdj((a) => ({ ...a, user_id: e.target.value }))} />
            <input className={inputCls} type="number" placeholder="Points (+/-)" value={adj.points} onChange={(e) => setAdj((a) => ({ ...a, points: e.target.value }))} />
            <input className={inputCls} placeholder="Reason" value={adj.reason} onChange={(e) => setAdj((a) => ({ ...a, reason: e.target.value }))} />
            <button
              data-testid="pe-admin-loyalty-adjust"
              onClick={async () => {
                try { await api("/admin/loyalty/adjust", { method: "POST", body: { user_id: adj.user_id, points: Number(adj.points), reason: adj.reason } }); setAdj({ user_id: "", points: "", reason: "" }); flash("Points adjusted and logged."); }
                catch (e) { alert(e.message); }
              }}
              className="rounded-full bg-pe-forest text-pe-ivory px-5 py-2 text-[10px] tracking-[0.2em] uppercase"
            >
              Adjust
            </button>
          </div>
        </div>
        <div className="rounded-sm border border-pe-sand/60 bg-white p-6 space-y-3">
          <p className="font-pe text-xl text-pe-forest">Reward rules</p>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#C59B27]" defaultChecked={s.first_time.enabled} onChange={(e) => saveSettings({ first_time: { ...s.first_time, enabled: e.target.checked } }, "First-time rule saved.")} /> First-time client — 20% off (applies to service price)</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#C59B27]" defaultChecked={s.fifth.enabled} onChange={(e) => saveSettings({ fifth: { ...s.fifth, enabled: e.target.checked } }, "Fifth-service rule saved.")} /> Fifth completed service — 50% off (applies to service price)</label>
          <label className="block text-sm"><span className="text-pe-muted text-[12px]">Referral 15% off — who receives it?</span>
            <select data-testid="pe-admin-referral-recipient" className={inputCls} defaultValue={s.referral.recipient}
              onChange={(e) => saveSettings({ referral: { ...s.referral, recipient: e.target.value } }, "Referral rule saved.")}>
              <option value="unset">Not configured — rewards stay pending (required before launch)</option>
              <option value="referrer">The referring client</option>
              <option value="referred">The new client</option>
              <option value="both">Both</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#C59B27]" defaultChecked={s.allow_stacking} onChange={(e) => saveSettings({ allow_stacking: e.target.checked }, "Stacking rule saved.")} /> Allow discounts to combine (off = one reward per booking)</label>
        </div>
      </div>
      <div className="rounded-sm border border-pe-sand/60 bg-white p-6">
        <p className="font-pe text-xl text-pe-forest mb-3">Referral activity</p>
        {data.referrals.map((r, i) => (
          <p key={i} className="text-[13px] text-pe-body border-b border-pe-sand/40 py-2">Code {r.code} · referrer {r.referrer_id} · referred {r.referred_id} · {r.status} · reward {r.reward_status}</p>
        ))}
        {!data.referrals.length && <p className="text-sm text-pe-muted">No referrals yet.</p>}
      </div>
    </div>
  );
}

function PromotionsTab({ data, reload, flash }) {
  const [form, setForm] = useState({ name: "", code: "", percent: 10 });
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="rounded-sm border border-pe-sand/60 bg-white p-6 space-y-3">
        <p className="font-pe text-xl text-pe-forest">New promotion</p>
        <input className={inputCls} placeholder="Name (e.g. Autumn Glow)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        <input className={inputCls} placeholder="Code (e.g. GLOW10)" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
        <label className="block text-sm"><span className="text-pe-muted text-[12px]">Percent off (applies to service price)</span>
          <input type="number" min="1" max="90" className={inputCls} value={form.percent} onChange={(e) => setForm((f) => ({ ...f, percent: e.target.value }))} />
        </label>
        <button
          data-testid="pe-admin-promo-create"
          onClick={async () => {
            try { await api("/admin/promotions", { method: "POST", body: { ...form, percent: Number(form.percent) } }); setForm({ name: "", code: "", percent: 10 }); await reload(); flash("Promotion created."); }
            catch (e) { alert(e.message); }
          }}
          className="rounded-full bg-pe-forest text-pe-ivory px-5 py-2 text-[10px] tracking-[0.2em] uppercase"
        >
          Create
        </button>
      </div>
      <div className="space-y-3">
        {data.promotions.map((p) => (
          <div key={p.promo_id} className="rounded-sm border border-pe-sand/60 bg-white p-5 flex justify-between items-center gap-3">
            <div>
              <p className="font-pe text-lg text-pe-forest">{p.name} — {p.percent}%</p>
              <p className="text-[11px] text-pe-muted">code {p.code || "none"} · {p.active ? "active" : "paused"} · stacking {p.stacking ? "allowed" : "no"}</p>
            </div>
            <button onClick={async () => { await api(`/admin/promotions/${p.promo_id}`, { method: "PATCH", body: { active: !p.active } }); await reload(); }} className="rounded-full border border-pe-brass/60 px-3 py-1 text-[10px] uppercase tracking-wider text-pe-forest">
              {p.active ? "Pause" : "Activate"}
            </button>
          </div>
        ))}
        {!data.promotions.length && <p className="text-sm text-pe-muted">No promotions yet.</p>}
      </div>
    </div>
  );
}

function CampaignsTab({ data, reload, flash }) {
  const [form, setForm] = useState({ subject: "", body_html: "" });
  const send = async (test) => {
    try {
      const r = await api("/admin/campaigns", { method: "POST", body: { ...form, test, origin_url: window.location.origin } });
      await reload();
      flash(test ? `Test sent to you.` : `Campaign sent to ${r.sent} opted-in client(s)${r.failed ? `, ${r.failed} failed` : ""}.`);
    } catch (e) { alert(e.message); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="rounded-sm border border-pe-sand/60 bg-white p-6 space-y-3">
        <p className="font-pe text-xl text-pe-forest">New campaign</p>
        <p className="text-[12px] text-pe-muted">Goes only to clients who opted in to marketing. Every email includes an unsubscribe link and the house branding. Appointment communications are never mixed in.</p>
        <input data-testid="pe-admin-campaign-subject" className={inputCls} placeholder="Subject" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
        <textarea data-testid="pe-admin-campaign-body" rows={6} className={inputCls} placeholder="Body (simple HTML welcome, e.g. <p>…</p>)" value={form.body_html} onChange={(e) => setForm((f) => ({ ...f, body_html: e.target.value }))} />
        <div className="flex gap-2">
          <button data-testid="pe-admin-campaign-test" onClick={() => send(true)} className="rounded-full border border-pe-brass/60 px-5 py-2 text-[10px] tracking-[0.2em] uppercase text-pe-forest">Send test to me</button>
          <button data-testid="pe-admin-campaign-send" onClick={() => send(false)} className="rounded-full bg-pe-forest text-pe-ivory px-5 py-2 text-[10px] tracking-[0.2em] uppercase">Send to opted-in clients</button>
        </div>
      </div>
      <div className="space-y-3">
        {data.campaigns.map((c) => (
          <div key={c.campaign_id} className="rounded-sm border border-pe-sand/60 bg-white p-5">
            <p className="font-pe text-lg text-pe-forest">{c.subject}</p>
            <p className="text-[11px] text-pe-muted">{c.test ? "test" : `${c.recipients} recipients`} · {c.sent} sent · {c.failed} failed · {new Date(c.at).toLocaleString()}</p>
          </div>
        ))}
        {!data.campaigns.length && <p className="text-sm text-pe-muted">No campaigns yet.</p>}
      </div>
    </div>
  );
}

function SettingsTab({ data, reload, flash }) {
  const s = data;
  const save = async (patch) => { await api("/admin/settings", { method: "PUT", body: patch }); await reload(); flash("Settings saved."); };
  const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="rounded-sm border border-pe-sand/60 bg-white p-6 space-y-4">
        <p className="font-pe text-xl text-pe-forest">Availability</p>
        {days.map((d) => (
          <div key={d} className="flex items-center gap-3">
            <span className="w-10 text-[12px] uppercase tracking-wider text-pe-olive">{d}</span>
            <input className={inputCls} placeholder="closed" defaultValue={s.business_hours[d]?.[0] || ""} onBlur={(e) => { const open = e.target.value; if (!open) return; const close = s.business_hours[d]?.[1] || "18:00"; save({ business_hours: { ...s.business_hours, [d]: [open, close] } }); }} />
            <span className="text-pe-muted">–</span>
            <input className={inputCls} placeholder="closed" defaultValue={s.business_hours[d]?.[1] || ""} onBlur={(e) => { const close = e.target.value; if (!close) return; const open = s.business_hours[d]?.[0] || "09:00"; save({ business_hours: { ...s.business_hours, [d]: [open, close] } }); }} />
            <button className="text-[10px] uppercase tracking-wider text-pe-muted hover:text-red-800" onClick={() => save({ business_hours: { ...s.business_hours, [d]: null } })}>close</button>
          </div>
        ))}
        <div className="grid grid-cols-3 gap-3 pt-2">
          <label className="text-[12px] text-pe-muted">Buffer (min)<input type="number" className={inputCls} defaultValue={s.buffer_minutes} onBlur={(e) => save({ buffer_minutes: Number(e.target.value) })} /></label>
          <label className="text-[12px] text-pe-muted">Lead time (hrs)<input type="number" className={inputCls} defaultValue={s.lead_hours} onBlur={(e) => save({ lead_hours: Number(e.target.value) })} /></label>
          <label className="text-[12px] text-pe-muted">Max per day<input type="number" className={inputCls} defaultValue={s.max_per_day} onBlur={(e) => save({ max_per_day: Number(e.target.value) })} /></label>
        </div>
        <label className="block text-[12px] text-pe-muted">Blocked dates (one per line, YYYY-MM-DD)
          <textarea rows={3} className={inputCls} defaultValue={(s.blocked_dates || []).join("\n")} onBlur={(e) => save({ blocked_dates: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} />
        </label>
      </div>
      <div className="rounded-sm border border-pe-sand/60 bg-white p-6 space-y-4">
        <p className="font-pe text-xl text-pe-forest">Reminders & policies</p>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#C59B27]" defaultChecked={s.reminders.email} onChange={(e) => save({ reminders: { ...s.reminders, email: e.target.checked } })} /> Email reminders (1 month / 1 week / 1 day)</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-[#C59B27]" defaultChecked={s.reminders.sms} onChange={(e) => save({ reminders: { ...s.reminders, sms: e.target.checked } })} /> SMS reminders (requires Twilio credentials in server config)</label>
        <div className="border-t border-pe-sand/50 pt-3 space-y-3" data-testid="pe-admin-cancellation-settings">
          <p className="text-[12px] uppercase tracking-[0.2em] text-pe-olive">Cancellation & rescheduling rules</p>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-[12px] text-pe-muted">Late window (hours)<input type="number" className={inputCls} defaultValue={s.cancellation?.window_hours ?? 24} onBlur={(e) => save({ cancellation: { ...s.cancellation, window_hours: Number(e.target.value) } })} /></label>
            <label className="text-[12px] text-pe-muted">Late-cancel fee ($)<input type="number" className={inputCls} defaultValue={s.cancellation?.late_fee ?? 20} onBlur={(e) => save({ cancellation: { ...s.cancellation, late_fee: Number(e.target.value) } })} /></label>
          </div>
          <label className="block text-[12px] text-pe-muted">Late rescheduling rule (within the window above)
            <select data-testid="pe-admin-late-reschedule-rule" className={inputCls} defaultValue={s.reschedule?.late_rule || "unpublished"}
              onChange={(e) => save({ reschedule: { ...s.reschedule, late_rule: e.target.value } })}>
              <option value="unpublished">Not published — reschedules proceed free, clients see "rule being finalized"</option>
              <option value="none">Published — no consequence, late reschedules are free</option>
              <option value="fee">Published — late reschedules pay the fee below</option>
            </select>
          </label>
          <label className="block text-[12px] text-pe-muted">Late-reschedule fee ($, only when "fee" is published)<input type="number" className={inputCls} defaultValue={s.reschedule?.late_fee ?? 20} onBlur={(e) => save({ reschedule: { ...s.reschedule, late_fee: Number(e.target.value) } })} /></label>
          <p className="text-[11px] text-pe-muted">Nothing here invents refunds or no-show charges — clients always see the wording below before they act.</p>
        </div>
        {["cancellation", "rescheduling", "privacy", "retention"].map((k) => (
          <label key={k} className="block text-[12px] text-pe-muted">{k[0].toUpperCase() + k.slice(1)} policy
            <textarea rows={3} className={inputCls} defaultValue={s.policies[k]} onBlur={(e) => save({ policies: { ...s.policies, [k]: e.target.value } })} />
          </label>
        ))}
      </div>
    </div>
  );
}
